'use client';

import { useIsDesktop } from '@/_hooks/useBreakpoint';
import { useTranslations, useLocale } from 'next-intl';
import Mobile from './responsive/mobile';
import { Box } from '@chakra-ui/react';
import { useBranchList } from '@/_service/branch.service';
import Desktop from './responsive/desktop';
import { useState, useMemo } from 'react';
import Header from '@/app/[locale]/(home)/_components/Header';
import UniversalHeader from '@/_components/Header';
import { FEATURED_BRANDS } from '@/_utils/Constants';
import { getAbsoluteLocaleUrl, getPublicSeoOrigin } from '@/i18n/urls';
import type { Locale } from '@/i18n/routing';
import SeoPageTitle from '@/_components/SeoPageTitle';

const Branches = ({
  initialBranches,
}: {
  initialBranches?: { count: string; branches: any[] } | null;
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const isDesktop = useIsDesktop();
  const [search, setSearch] = useState<string>('');
  const [activeBrandId, setActiveBrandId] = useState<string>(() => FEATURED_BRANDS.yaponamama);

  const { data } = useBranchList({
    params: {
      page: 1,
      limit: 100,
      search: search,
    },
    props: { enabled: true },
    shipperId: activeBrandId,
    initialData:
      !search && activeBrandId === FEATURED_BRANDS.yaponamama
        ? initialBranches ?? undefined
        : undefined,
  });

  const breadcrumbJson = useMemo(() => {
    const domain = getPublicSeoOrigin();

    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          item: {
            '@id': getAbsoluteLocaleUrl(domain, locale as Locale, '/'),
            name: t('home'),
          },
        },
        {
          '@type': 'ListItem',
          position: 2,
          item: {
            '@id': getAbsoluteLocaleUrl(domain, locale as Locale, '/branches'),
            name: t('branches'),
          },
        },
      ],
    };
  }, [locale, t]);

  return (
    <Box bg={'background'} minH="100vh">
      {isDesktop ? (
        <>
          <Header />
          <Desktop
            data={data?.branches}
            search={search}
            setSearch={setSearch}
            activeBrandId={activeBrandId}
            setActiveBrandId={setActiveBrandId}
          />
        </>
      ) : (
        <>
          <UniversalHeader backLink='/profile' title={t('branches')} />
          <SeoPageTitle>{t('branches')}</SeoPageTitle>
          <Mobile data={data?.branches}
            activeBrandId={activeBrandId}
            setActiveBrandId={setActiveBrandId}
          />
        </>
      )}

      {/* ✅ Breadcrumb structured data */}
      {breadcrumbJson && (
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breadcrumbJson),
          }}
        />
      )}
    </Box>
  );
};

export default Branches;
